import { useState, useEffect, useRef } from 'react';
import { Search, Mail, Download, ChevronLeft, User, LogOut } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';

import VariantSummary from './VariantSummary';
import PathogenicityScores from './PathogenicityScores';
import PopulationFrequencies from './PopulationFrequencies';
import ClinVarSection from './ClinVarSection';
import ACMGClassification from './ACMGClassification';
import GeneInfo from './GeneInfo';
import Publications from './Publications';
import GenomeBrowser from './GenomeBrowser';
import PhenomeAI from './PhenomeAI';
import MGSScore from './MGSScore';
import UserInputPanel from './UserInputPanel';
import EmailModal from './EmailModal';

const NAV_SECTIONS = [
  { id: 'summary', label: 'Summary' },
  { id: 'scores', label: 'Scores' },
  { id: 'population', label: 'Population' },
  { id: 'clinvar', label: 'ClinVar' },
  { id: 'acmg', label: 'ACMG' },
  { id: 'gene', label: 'Gene' },
  { id: 'publications', label: 'Publications' },
  { id: 'genome', label: 'Genome' },
  { id: 'phenomeai', label: 'PhenomeAI' },
  { id: 'mgs', label: 'MGS' },
];

function StickyHeader({ variant, query, onNewSearch, onEmailModal, onAuthOpen }) {
  const { user, signOut } = useAuth();
  const [searchVal, setSearchVal] = useState(query || '');
  const [activeSection, setActiveSection] = useState('summary');

  useEffect(() => {
    const handleScroll = () => {
      for (let i = NAV_SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(NAV_SECTIONS[i].id);
        if (el && el.getBoundingClientRect().top <= 90) {
          setActiveSection(NAV_SECTIONS[i].id);
          return;
        }
      }
      setActiveSection('summary');
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchVal.trim()) onNewSearch();
  };

  return (
    <header className="sticky top-0 z-40 shadow-lg" style={{ background: '#021f30' }}>
      {/* Top bar */}
      <div className="flex items-center gap-4 px-4 sm:px-6 py-3 border-b border-white/10">
        {/* Logo */}
        <button
          onClick={onNewSearch}
          className="shrink-0 hover:opacity-80 transition-opacity"
          aria-label="Haploix home"
        >
          <Logo size="sm" />
        </button>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex-1 flex items-center relative max-w-xl">
          <Search size={15} className="absolute left-3 text-white/40 pointer-events-none" />
          <input
            type="text"
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            className="w-full bg-white/10 text-white placeholder-white/40 pl-9 pr-4 py-2 rounded-xl text-sm outline-none focus:bg-white/15 transition-all font-mono border border-transparent focus:border-white/20"
            placeholder="New search..."
          />
        </form>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <button
            onClick={onEmailModal}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition-all hover:brightness-110 hidden sm:flex"
            style={{ background: '#f93441' }}
          >
            <Mail size={13} />
            Request VCF Report
          </button>
          <button
            onClick={onEmailModal}
            className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-all sm:hidden"
          >
            <Mail size={16} />
          </button>
          <button className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-all"
            title="Download report">
            <Download size={16} />
          </button>
          <button
            onClick={onNewSearch}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white/60 hover:text-white border border-white/20 hover:border-white/40 transition-all"
          >
            <ChevronLeft size={14} />
            <span className="hidden sm:block">New Search</span>
          </button>
          {user ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                style={{ background: '#f93441' }}>
                {(user.user_metadata?.full_name || user.email || '?')[0].toUpperCase()}
              </div>
              <button onClick={signOut}
                className="p-2 rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-all" title="Sign out">
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <button onClick={onAuthOpen}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white/60 hover:text-white border border-white/20 hover:border-white/40 transition-all">
              <User size={14} /> Sign in
            </button>
          )}
        </div>
      </div>

      {/* Section nav */}
      <nav className="flex items-center gap-0 px-4 sm:px-6 overflow-x-auto scrollbar-hide">
        {NAV_SECTIONS.map((sec) => (
          <a
            key={sec.id}
            href={`#${sec.id}`}
            className={`px-3 sm:px-4 py-2.5 text-xs font-semibold border-b-2 transition-all whitespace-nowrap ${
              activeSection === sec.id
                ? 'nav-link-active text-coral border-coral'
                : 'border-transparent text-white/50 hover:text-white hover:border-white/30'
            }`}
          >
            {sec.label}
          </a>
        ))}
      </nav>
    </header>
  );
}

export default function ResultsPage({ variant, query, onNewSearch, onAuthOpen }) {
  const [emailOpen, setEmailOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: '#f0f4f8' }}>
      <StickyHeader
        variant={variant}
        query={query}
        onNewSearch={onNewSearch}
        onEmailModal={() => setEmailOpen(true)}
        onAuthOpen={onAuthOpen}
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-col gap-5">
        {/* User context enrichment panel */}
        <UserInputPanel />

        {/* Variant Summary */}
        <VariantSummary variant={variant} />

        {/* MGS Score */}
        <MGSScore mgs={variant.mgs} />

        {/* Pathogenicity Scores */}
        <PathogenicityScores scores={variant.scores} />

        {/* Population Frequencies */}
        <PopulationFrequencies gnomad={variant.gnomad} />

        {/* ClinVar */}
        <ClinVarSection clinvar={variant.clinvar} />

        {/* ACMG */}
        <ACMGClassification acmg={variant.acmg} />

        {/* Gene Info */}
        <GeneInfo gene={variant.gene_info} />

        {/* Genome Browser */}
        <GenomeBrowser variant={variant} />

        {/* PhenomeAI */}
        <PhenomeAI data={variant.phenomeAI} />

        {/* Publications */}
        <Publications publications={variant.publications} />

        {/* Footer CTA */}
        <div
          className="rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ background: '#021f30' }}
        >
          <div>
            <h3 className="text-white font-bold text-base">Need a comprehensive clinical report?</h3>
            <p className="text-white/60 text-sm mt-0.5">
              Get a full annotated VCF with ACMG evidence, literature review, and clinician PDF.
            </p>
          </div>
          <button
            onClick={() => setEmailOpen(true)}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm transition-all hover:brightness-110 active:scale-95 shrink-0"
            style={{ background: '#f93441', boxShadow: '0 4px 16px rgba(249,52,65,0.25)' }}
          >
            <Mail size={16} />
            Request Full VCF Analysis
          </button>
        </div>
      </main>

      <EmailModal isOpen={emailOpen} onClose={() => setEmailOpen(false)} />
    </div>
  );
}
